import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Server, FileText, Users, LogOut, ChevronLeft, ChevronDown, ChevronRight, Menu, Shield, Layers, BarChart2, ClipboardList, Receipt, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
export function Sidebar({
  isOpen,
  setIsOpen
}: SidebarProps) {
  const {
    user,
    logout
  } = useAuth();
  const location = useLocation();
  const toggleSidebar = () => setIsOpen(!isOpen);
  const isReportsActive = location.pathname.startsWith('/reports');
  const [reportsExpanded, setReportsExpanded] = useState(isReportsActive);
  const topNavItems = [{
    name: 'Overview',
    path: '/',
    icon: LayoutDashboard
  }, {
    name: 'Technical Dashboard',
    path: '/technical',
    icon: Server
  }, {
    name: 'Billing Dashboard',
    path: '/billing',
    icon: FileText
  }];
  const reportSubItems = [{
    name: 'All Applications',
    path: '/reports/all-applications',
    icon: ClipboardList
  }, {
    name: 'Payment Breakdown',
    path: '/reports/payment-breakdown',
    icon: Receipt
  }, {
    name: 'Reconciliation',
    path: '/reports/reconciliation',
    icon: RefreshCw
  }];
  const bottomNavItems = [{
    name: 'Users',
    path: '/users',
    icon: Users
  }, {
    name: 'Roles',
    path: '/roles',
    icon: Shield
  }, {
    name: 'Services',
    path: '/services',
    icon: Layers
  }];
  return <aside className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 bg-slate-900 text-white border-r border-slate-800 flex flex-col ${isOpen ? 'w-64' : 'w-20'}`} data-id="element-49">
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800 flex-shrink-0" data-id="element-50">
        {isOpen ? <div className="flex items-center space-x-2" data-id="element-51">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center font-bold text-lg" data-id="element-52">
              L
            </div>
            <span className="font-bold text-lg tracking-tight" data-id="element-53">
              LISF Portal
            </span>
          </div> : <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center font-bold text-lg mx-auto" data-id="element-54">
            L
          </div>}
        <button onClick={toggleSidebar} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors" data-id="element-55">
          {isOpen ? <ChevronLeft size={20} data-id="element-56" /> : <Menu size={20} data-id="element-57" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-id="element-58">
        {/* Top items */}
        {topNavItems.map(item => <NavLink key={item.path} to={item.path} end={item.path === '/'} className={({
        isActive
      }) => `
              flex items-center px-3 py-2.5 rounded-lg transition-colors group relative
              ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
            `} data-id="element-59">
            <item.icon size={20} className={`flex-shrink-0 ${isOpen ? 'mr-3' : 'mx-auto'}`} data-id="element-60" />
            {isOpen && <span className="font-medium text-sm" data-id="element-61">{item.name}</span>}
            {!isOpen && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-62">
                {item.name}
              </div>}
          </NavLink>)}

        {/* Reports Section with submenu */}
        <div data-id="element-63">
          <button onClick={() => isOpen && setReportsExpanded(!reportsExpanded)} className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-colors group relative
              ${isReportsActive ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
            `} data-id="element-64">
            <BarChart2 size={20} className={`flex-shrink-0 ${isOpen ? 'mr-3' : 'mx-auto'}`} data-id="element-65" />
            {isOpen && <>
                <span className="font-medium text-sm flex-1 text-left" data-id="element-66">
                  Reports
                </span>
                {reportsExpanded ? <ChevronDown size={16} data-id="element-67" /> : <ChevronRight size={16} data-id="element-68" />}
              </>}
            {!isOpen && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-69">
                Reports
              </div>}
          </button>

          {/* Sub-items */}
          {isOpen && reportsExpanded && <div className="mt-1 ml-3 pl-3 border-l border-slate-700 space-y-1" data-id="element-70">
              {reportSubItems.map(sub => <NavLink key={sub.path} to={sub.path} className={({
            isActive
          }) => `
                    flex items-center px-3 py-2 rounded-lg transition-colors text-sm
                    ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                  `} data-id="element-71">
                  <sub.icon size={16} className="mr-2.5 flex-shrink-0" data-id="element-72" />
                  <span data-id="element-73">{sub.name}</span>
                </NavLink>)}
            </div>}

          {/* Collapsed: show sub-items as tooltips */}
          {!isOpen && <div className="mt-1 space-y-1" data-id="element-74">
              {reportSubItems.map(sub => <NavLink key={sub.path} to={sub.path} className={({
            isActive
          }) => `
                    flex items-center px-3 py-2.5 rounded-lg transition-colors group relative
                    ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                  `} data-id="element-75">
                  <sub.icon size={18} className="mx-auto" data-id="element-76" />
                  <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-77">
                    {sub.name}
                  </div>
                </NavLink>)}
            </div>}
        </div>

        {/* Bottom items */}
        <div className="pt-1 border-t border-slate-800 mt-1" data-id="element-78">
          {bottomNavItems.map(item => <NavLink key={item.path} to={item.path} className={({
          isActive
        }) => `
                flex items-center px-3 py-2.5 rounded-lg transition-colors group relative mt-1
                ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
              `} data-id="element-79">
              <item.icon size={20} className={`flex-shrink-0 ${isOpen ? 'mr-3' : 'mx-auto'}`} data-id="element-80" />
              {isOpen && <span className="font-medium text-sm" data-id="element-81">{item.name}</span>}
              {!isOpen && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-82">
                  {item.name}
                </div>}
            </NavLink>)}
        </div>
      </nav>

      {/* Footer / User Info */}
      <div className="flex-shrink-0 p-4 border-t border-slate-800 bg-slate-900" data-id="element-83">
        <div className={`flex items-center ${isOpen ? 'justify-between' : 'justify-center'}`} data-id="element-84">
          {isOpen && <div className="flex items-center space-x-3 overflow-hidden" data-id="element-85">
              <img src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name || 'User'}`} alt="User" className="w-8 h-8 rounded-full bg-slate-700 flex-shrink-0" data-id="element-86" />
              <div className="flex-1 min-w-0" data-id="element-87">
                <p className="text-sm font-medium truncate" data-id="element-88">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate" data-id="element-89">{user?.role}</p>
              </div>
            </div>}
          <button onClick={logout} className={`p-2 rounded-lg hover:bg-red-500/10 hover:text-red-500 text-slate-400 transition-colors ${!isOpen && 'mx-auto'}`} title="Logout" data-id="element-90">
            <LogOut size={20} data-id="element-91" />
          </button>
        </div>
      </div>
    </aside>;
}